import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import InformationFill from '@/assets/icons/information_fill.svg?component';
import { Alert } from '@/components/atoms/Alert';
import { AmountRatioWithText } from '@/components/atoms/AmountRatioWithText';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import PageLayout from '@/components/Layout/PageLayout';
import EmptyState from '@/components/molecules/EmptyState';
import { DEFAULT_SORT_BY, type SortBy } from '@/components/molecules/SortByDropdown';
import type { OverviewLocation } from '@/domain/client';
import type { LocationsLicenseOverview } from '@/domain/licenses';
import type { ApiError } from '@/domain/shared/errors';

import RegionOverviewDownload from './components/RegionOverviewDownload';
import SortedLocations from './components/SortedLocations';
import { amountOfActiveBioscopes, totalAmountOfActiveBioscopes } from './regionOverviewMetrics';

import styles from './RegionOverview.module.scss';

interface OverviewProps {
    clientId: number;
    locations: OverviewLocation[];
    locationLicenses: LocationsLicenseOverview[];
    hasAccessToMarkHistorical: boolean;
    markHistorical: (cageId: number, historical: boolean) => Promise<unknown>;
    isOptoScaleAdmin?: boolean;
    containerIsLoading: boolean;
    containerError: ApiError | null;
    clientName?: string | null;
}
export default function RegionOverviewView(props: OverviewProps) {
    const {
        clientId,
        locations,
        locationLicenses,
        markHistorical,
        hasAccessToMarkHistorical,
        isOptoScaleAdmin = false,
        clientName = '',
    } = props;
    const { t } = useTranslation();
    //The block below ensures we scroll to the correct location when the page is loaded
    //It is a workaround for the fact that the page is rendered before the data is loaded
    //and therefore the page does not know where to scroll to
    //The block below is based on https://ericdcobb.medium.com/scrolling-to-an-anchor-in-react-when-your-elements-are-rendered-asynchronously-8c64f77b5f34
    const [showInactive, setShowInactive] = useState(false);

    const [perLocationSort, setPerLocationSort] = useState<Record<number, SortBy>>({});

    const handleLocationSortChange = useCallback((locationId: number, next: SortBy) => {
        setPerLocationSort((prev) => ({ ...prev, [locationId]: next }));
    }, []);

    const sortForLocation = useCallback(
        (locationId: number): SortBy => perLocationSort[locationId] ?? DEFAULT_SORT_BY,
        [perLocationSort]
    );

    const { ref } = useInView({
        /* Optional options */
        initialInView: false,
        triggerOnce: true,
        rootMargin: '100px',
        onChange: (inView) => {
            if (inView) {
                setShowInactive(true);
            }
        },
    });

    locations.sort((a, b) => a.locationName.localeCompare(b.locationName));
    const activeLocations = useMemo(
        () => locations.filter((location) => location.activeCages.length > 0),
        [locations]
    );

    const maxDate = useCallback((cages: { cageData: { weight?: { day: string } } }[]) => {
        const candidateCages = cages.filter((cage) => cage.cageData.weight?.day);
        const max = Math.max(
            ...candidateCages.map((cage) => new Date(cage.cageData.weight?.day ?? '').valueOf())
        );
        return Number.isNaN(max) ? -1 : max;
    }, []);

    const inactiveLocations = useMemo(
        () =>
            locations
                .filter((location) => location.activeCages.length === 0)
                .sort((a, b) => maxDate(b.cages) - maxDate(a.cages)),
        [locations, maxDate]
    );

    if (!locations) {
        return (
            <PageLayout
                title={clientName ? clientName : ''}
                subtitle={t('regionOverview.regionOverview.regionOverview')}>
                <EmptyState
                    title={t('regionOverview.regionOverview.noLocations')}
                    headingLevel="h2"
                    size="spacious"
                />
            </PageLayout>
        );
    }

    return (
        <PageLayout
            title={clientName ? clientName : ''}
            subtitle={t('regionOverview.regionOverview.regionOverview')}
            className={styles.siteOverviewPage}
            notice={
                clientId === 59 ? (
                    <Alert
                        tone="info"
                        live="off"
                        icon={<SvgIcon icon={InformationFill} color="currentColor" />}>
                        {t('regionOverview.regionOverview.infoTextForChile')}
                    </Alert>
                ) : null
            }
            actions={
                <div className={styles.pageTitleRowButtonContainer}>
                    <RegionOverviewDownload locations={locations} />
                </div>
            }>
            <div className={styles.activeinactivebioscopes}>
                <AmountRatioWithText
                    leftSideAmount={amountOfActiveBioscopes(locations)}
                    rightSideAmount={totalAmountOfActiveBioscopes(locations)}
                    text={'regionOverview.common.bioscopesActivelyMeasuring'}
                />
            </div>

            <SortedLocations
                renderLazy={true}
                locations={activeLocations}
                locationLicenses={locationLicenses}
                clientId={clientId}
                hasAccessToMarkHistorical={hasAccessToMarkHistorical}
                markHistorical={markHistorical}
                isOptoScaleAdmin={isOptoScaleAdmin}
                sortForLocation={sortForLocation}
                onLocationSortChange={handleLocationSortChange}
            />
            <div ref={ref}>
                <hr className={styles.divider} data-label={t('common.labels.inactiveLocations')} />
                <SortedLocations
                    renderLazy={showInactive}
                    locations={inactiveLocations}
                    locationLicenses={locationLicenses}
                    isActive={false}
                    clientId={clientId}
                    hasAccessToMarkHistorical={hasAccessToMarkHistorical}
                    markHistorical={markHistorical}
                    isOptoScaleAdmin={isOptoScaleAdmin}
                    sortForLocation={sortForLocation}
                    onLocationSortChange={handleLocationSortChange}
                />
            </div>
        </PageLayout>
    );
}
